import { useAuth } from 'lib/context';
import { pushToDataLayer } from 'lib/utils/pushToDataLayer';
import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const EXCLUDED_PATHS: string[] = ['/send-and-share'];

const PAGE_SECTION_MAP: Record<string, string> = {
  users: 'My Team',
  overview: 'My Stats',
  custom: 'Custom Reports',
  'activity-feed': 'Activity Feed',
  'landing-pages': 'Landing Pages',
  'service-pages': 'Sales Pages',
  'calls-to-action': 'Calls to Action',
  profile_edituserinfo: 'My Profile',
  profile_editcompanyinfo: 'Company Profile',
  profile_videoattributes: 'Video Attributes',
  'recently-deleted': 'Recently Deleted',
  details: 'Manage',
  trim: 'Trimming',
};

const PAGE_LOCATION_MAP: Record<string, string> = {
  classic: 'Library',
};

const capitalizeFirst = (str?: string): string | undefined =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : undefined;

const mapPageSection = (section: string | undefined): string | undefined => {
  if (!section) {
    return undefined;
  }
  // skip IDs
  if (!isNaN(Number(section))) {
    return undefined;
  }
  const key = section.toLowerCase();
  return PAGE_SECTION_MAP[key] || capitalizeFirst(section);
};

const mapPageLocation = (location: string | undefined): string | undefined => {
  if (!location) {
    return undefined;
  }
  const key = location.toLowerCase();
  return PAGE_LOCATION_MAP[key] || capitalizeFirst(location);
};

export function useTrackPageView() {
  const { userData } = useAuth();
  const location = useLocation();
  const { pathname } = location;

  const trackPageViewManually = useCallback(
    (eventData?: Record<string, any>) => {
      if (!userData) {
        return;
      }

      const pathParts = pathname.split('/').filter(Boolean);
      const rawLocation = pathParts[0] || undefined;
      const rawSection = pathParts[pathParts.length - 1] || undefined;

      const pageLocation = mapPageLocation(rawLocation);
      const pageSection = mapPageSection(rawSection);

      const payload = {
        event: 'covideo-pageview',
        page_application: 'CDK Video Whitelabel',
        user_id: userData.email,
        page_url: window.location.href,
        page_location: pageLocation,
        page_section: pageSection,
        is_internal_user:
          userData?.email?.includes('cdk') ||
          userData?.email?.includes('covideo'),
        ...eventData,
      };

      pushToDataLayer(payload);
    },
    [location.pathname, userData]
  );

  useEffect(() => {
    if (!userData) {
      return;
    }

    // Skip auto-track if path is in exclusion list
    const isExcluded = EXCLUDED_PATHS.some(excluded =>
      pathname.includes(excluded)
    );
    if (isExcluded) {
      return;
    }

    trackPageViewManually();
  }, [location.pathname, userData, trackPageViewManually]);

  return { trackPageViewManually };
}
